import React from 'react';

// Inject keyframes for pulse animation
const injectKeyframes = (): void => {
  const styleId = 'skeleton-keyframes';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes skeletonPulse {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  `;
  document.head.appendChild(style);
};

/* =============================================================================
   Base Skeleton Component
   ============================================================================= */

interface SkeletonProps {
  /** Width of the skeleton (CSS value, e.g., '100%', '200px') */
  width?: string | number;
  /** Height of the skeleton (CSS value, e.g., '20px', '1rem') */
  height?: string | number;
  /** Border radius (CSS value, default: '4px') */
  borderRadius?: string | number;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Additional class name */
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = '1rem',
  borderRadius = 4,
  style,
  className,
}) => {
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  const skeletonStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    borderRadius: typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius,
    backgroundColor: 'var(--bg-medium, #3d3d3d)',
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
    ...style,
  };

  return <div style={skeletonStyle} className={className} aria-hidden="true" />;
};

/* =============================================================================
   SkeletonText - For text line placeholders
   ============================================================================= */

interface SkeletonTextProps {
  /** Number of text lines to display */
  lines?: number;
  /** Gap between lines (CSS value, default: '0.5rem') */
  gap?: string | number;
  /** Width of the last line (CSS value, default: '60%') */
  lastLineWidth?: string | number;
  /** Height of each line (CSS value, default: '1rem') */
  lineHeight?: string | number;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
}

const SkeletonText: React.FC<SkeletonTextProps> = ({
  lines = 3,
  gap = '0.5rem',
  lastLineWidth = '60%',
  lineHeight = '1rem',
  style,
}) => {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: typeof gap === 'number' ? `${gap}px` : gap,
    ...style,
  };

  return (
    <div style={containerStyle} aria-hidden="true">
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          width={index === lines - 1 ? lastLineWidth : '100%'}
          height={lineHeight}
        />
      ))}
    </div>
  );
};

/* =============================================================================
   SkeletonTable - For table placeholders
   ============================================================================= */

interface SkeletonTableProps {
  /** Number of rows to display */
  rows?: number;
  /** Number of columns to display */
  columns?: number;
  /** Whether to show a header row (slightly different styling) */
  showHeader?: boolean;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
}

const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  showHeader = true,
  style,
}) => {
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  const containerStyle: React.CSSProperties = {
    width: '100%',
    borderRadius: 8,
    backgroundColor: 'var(--bg-dark, #2d2d2d)',
    overflow: 'hidden',
    ...style,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1rem',
    padding: '12px 15px',
    borderBottom: '1px solid var(--border-color, #4d4d4d)',
  };

  const headerRowStyle: React.CSSProperties = {
    ...rowStyle,
    backgroundColor: 'var(--bg-medium, #3d3d3d)',
  };

  const cellStyle: React.CSSProperties = {
    flex: 1,
  };

  const totalRows = showHeader ? rows : rows;

  return (
    <div style={containerStyle} aria-hidden="true">
      {showHeader && (
        <div style={headerRowStyle}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div key={`header-${colIndex}`} style={cellStyle}>
              <Skeleton height="1rem" width="70%" />
            </div>
          ))}
        </div>
      )}
      {Array.from({ length: totalRows }).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex}`}
          style={{
            ...rowStyle,
            borderBottom: rowIndex === totalRows - 1 ? 'none' : rowStyle.borderBottom,
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div key={`cell-${rowIndex}-${colIndex}`} style={cellStyle}>
              <Skeleton
                height="1rem"
                width={colIndex === 0 ? '80%' : colIndex === columns - 1 ? '50%' : '65%'}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/* =============================================================================
   SkeletonCard - For card placeholders
   ============================================================================= */

interface SkeletonCardProps {
  /** Whether to show an image placeholder at the top */
  showImage?: boolean;
  /** Height of the image placeholder (CSS value, default: '150px') */
  imageHeight?: string | number;
  /** Number of text lines in the card body */
  textLines?: number;
  /** Whether to show action buttons at the bottom */
  showActions?: boolean;
  /** Additional inline styles for the card */
  style?: React.CSSProperties;
}

const SkeletonCard: React.FC<SkeletonCardProps> = ({
  showImage = false,
  imageHeight = 150,
  textLines = 3,
  showActions = false,
  style,
}) => {
  React.useEffect(() => {
    injectKeyframes();
  }, []);

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--bg-dark, #2d2d2d)',
    borderRadius: 8,
    border: '1px solid var(--border-color, #4d4d4d)',
    overflow: 'hidden',
    ...style,
  };

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: typeof imageHeight === 'number' ? `${imageHeight}px` : imageHeight,
    backgroundColor: 'var(--bg-medium, #3d3d3d)',
    animation: 'skeletonPulse 1.5s ease-in-out infinite',
  };

  const bodyStyle: React.CSSProperties = {
    padding: '1rem',
  };

  const titleStyle: React.CSSProperties = {
    marginBottom: '1rem',
  };

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid var(--border-color, #4d4d4d)',
  };

  return (
    <div style={cardStyle} aria-hidden="true">
      {showImage && <div style={imageStyle} />}
      <div style={bodyStyle}>
        <div style={titleStyle}>
          <Skeleton height="1.25rem" width="60%" />
        </div>
        <SkeletonText lines={textLines} />
        {showActions && (
          <div style={actionsStyle}>
            <Skeleton width={80} height={32} borderRadius={4} />
            <Skeleton width={80} height={32} borderRadius={4} />
          </div>
        )}
      </div>
    </div>
  );
};

export { Skeleton, SkeletonText, SkeletonTable, SkeletonCard };
export default Skeleton;
